/**
 * The words this app uses on screen.
 *
 * Status names in the database are for the scheduler (`awaiting_outcome`,
 * `not_a_callback`); the words here are for a person scanning a table. They are
 * collected in one file so the queue tab, the badge, the detail sheet and the
 * account card can never disagree about what a state is called — and each carries
 * a one-line explanation, because "Needs attention" is only useful if the UI can
 * also say what the attention is for.
 */

export type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "muted";

export type StatusMeta = { label: string; tone: Tone; hint: string };

export const STATUS: Readonly<Record<string, StatusMeta>> = {
  scheduled: {
    // Neutral, not info: a whole column of blue reads as a column of links, and
    // "waiting for its time" is the resting state here, not something to notice.
    label: "Scheduled",
    tone: "neutral",
    hint: "Waiting for its time. The scheduler will dial it inside calling hours.",
  },
  dialing: {
    label: "Dialling",
    tone: "warning",
    hint: "We are placing the call to 8loop right now.",
  },
  awaiting_outcome: {
    label: "Awaiting outcome",
    tone: "warning",
    hint: "The call was placed. We are asking 8loop how it ended before deciding whether to retry.",
  },
  completed: {
    label: "Called",
    tone: "success",
    hint: "The call connected and this callback is closed.",
  },
  needs_attention: {
    label: "Needs attention",
    tone: "danger",
    hint: "We could not decide this one on our own — a person has to look.",
  },
  failed: {
    label: "Failed",
    tone: "danger",
    hint: "We stopped trying: either 8loop refused the call or the attempt cap was reached.",
  },
  cancelled: {
    label: "Cancelled",
    tone: "muted",
    hint: "Cancelled by hand. Nothing further will be dialled.",
  },
  not_a_callback: {
    label: "Not a callback",
    tone: "muted",
    hint: "Recorded for history only — this outcome is never called back.",
  },
};

export function statusMeta(status: string): StatusMeta {
  return STATUS[status] ?? { label: status, tone: "neutral", hint: "" };
}

/**
 * Tone → Tailwind text colour.
 *
 * 8loop states status as **coloured text, never a badge** (design-system.md §4.2);
 * badges there mean identifiers — a channel name, a version. Following that is most
 * of what makes a table look like it belongs in 8loop rather than beside it.
 */
export const TONE_CLASS: Readonly<Record<Tone, string>> = {
  neutral: "text-foreground",
  info: "text-info",
  success: "text-success font-medium",
  warning: "text-warning",
  danger: "text-destructive font-medium",
  muted: "text-muted-foreground",
};

export const GROUPS = [
  {
    value: "due",
    label: "Due",
    hint: "Scheduled and waiting for their time.",
  },
  {
    value: "in_flight",
    label: "In flight",
    hint: "Dialling now, or waiting on 8loop to tell us how the call ended.",
  },
  { value: "called", label: "Called", hint: "Connected and closed." },
  {
    value: "attention",
    label: "Needs attention",
    hint: "We could not schedule or could not read the outcome.",
  },
  { value: "failed", label: "Failed", hint: "Gave up — refused, or out of attempts." },
  { value: "closed", label: "Closed", hint: "Cancelled by hand, or never callable." },
] as const;

/** Why a callback ended up at the time it did. */
export const SCHEDULE_REASON: Readonly<Record<string, string>> = {
  as_requested: "at the time they asked for",
  requested_exact: "at the time they asked for",
  requested_part_of_day: "from the part of day they named",
  requested_date_only: "on the date they named, at this account's default hour",
  moved_into_window: "moved to the next open calling hour",
  defaulted_no_time: "no time was given, so the account default applied",
  min_lead: "pushed out to leave a minimum lead time",
  retry_unanswered: "a retry after nobody answered",
  manual: "set by hand",
};

/**
 * What 8loop said when the call ended.
 *
 * These keys are 8loop's `EndReason` enum verbatim — UPPER_SNAKE, exactly as the API
 * returns them. This map previously held invented lowercase names (`hangup`, `busy`,
 * `failed`, `cancelled`), so every real value missed the lookup and the detail sheet
 * printed the raw enum twice: "USER_HANGUP  USER_HANGUP".
 *
 * The glosses are written from a measurement of 1,673 real calls across three tenants,
 * so they say what the value *means in practice* rather than restating its name. Note
 * especially that there is no BUSY member — a busy line arrives as NO_ANSWER.
 */
export const END_REASON: Readonly<Record<string, string>> = {
  // Somebody was on the line.
  USER_HANGUP: "the person hung up — usually the normal end of a call, but a quarter of these last under 8 seconds",
  AGENT_HANGUP: "the agent ended the call, normally because it was finished",
  CALL_TRANSFERRED: "the call was handed to a human",
  MAX_CALL_DURATION_REACHED: "the call hit its length limit while still going",
  // Something picked up, but no conversation happened.
  USER_SILENCE: "answered, then nobody spoke — never under 40 seconds, so it is a long silence rather than a quick hangup",
  ECHO_DETECTED: "the line echoed the agent back at itself; nothing was said",
  VOICEMAIL: "an answering machine picked up — detected reliably, almost always inside 25 seconds",
  // Nobody picked up.
  NO_ANSWER: "nobody answered. A busy line also arrives this way — 8loop has no separate busy reason",
  // The call never really left.
  INVALID_NUMBER: "the number is not callable, so it is never retried",
  PROVIDER_ERROR: "the carrier refused the call, typically inside half a second — the phone never rang",
  CALL_TIMEOUT: "8loop gave up waiting on the call",
  CANCELED: "the call was cancelled before it went anywhere",
};

/**
 * The app's own outcome vocabulary, in words.
 *
 * Mirrors `OUTCOME_LABELS` in server/src/lib/outcomes.ts. Duplicated deliberately:
 * the configuration screens get each rule's label from the server (`OutcomeRule.label`),
 * but a bare outcome slug also arrives on a callback row — `reported_outcome`, what the
 * tenant's own webhook told us — and there is no rule object beside it to borrow a name
 * from. Printing `never_spoke` at somebody is not a label.
 */
export const OUTCOME: Readonly<Record<string, string>> = {
  callback_requested: "Callback requested",
  voicemail: "Voicemail",
  no_answer: "No answer",
  never_spoke: "Never spoke",
  dropped: "Call dropped",
  not_interested: "Not interested",
  dnc: "Do not call",
  wrong_number: "Wrong number",
};

/**
 * What *our* dial came to — `dial_outcome`, which is a different question from the
 * outcome that created the callback in the first place.
 *
 * These are phrased as a verdict on the call we placed, because that is the column's
 * question ("What we made of it"). Two families of value land here, from
 * `dialOutcomeFromEndReason`: our own outcome names for the ends we understand, and a
 * lowercased 8loop end reason for the ones we deliberately refuse to fold into
 * `no_answer` (`provider_error`, `call_timeout`). Both are covered.
 */
export const DIAL_OUTCOME: Readonly<Record<string, string>> = {
  answered: "we reached them — the call was answered and ran its course",
  voicemail: "we got their voicemail",
  no_answer: "nobody answered",
  never_spoke: "something answered, but nobody spoke",
  wrong_number: "the number is not callable — it will not be tried again",
  // Not folded into "no answer" on purpose: measured at 0.1–0.5s, so the carrier
  // rejected the leg and the phone never rang. Saying "no answer" would point an
  // FDE at the customer when the problem is the trunk.
  provider_error: "the carrier refused the call — the phone never rang",
  call_timeout: "8loop gave up waiting on the call",
  canceled: "the call was cancelled before it went anywhere",
  invalid_number: "the number is not callable — it will not be tried again",
};

/** Which config level a resolved retry number came from. Server sends the bare word. */
export const RETRY_SOURCE: Readonly<Record<string, string>> = {
  account: "the account default, under Retries & defaults",
  outcome: "this outcome's own override, under Outcomes",
};

export function weekdayName(iso: number): string {
  return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][iso - 1] ?? `Day ${iso}`;
}

export function weekdayShort(iso: number): string {
  return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][iso - 1] ?? `${iso}`;
}

/** "30 minutes", "2 hours", "1 hour 30 minutes" — for delays an FDE typed in minutes. */
export function minutesToWords(total: number): string {
  if (total < 60) return `${total} minute${total === 1 ? "" : "s"}`;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  const h = `${hours} hour${hours === 1 ? "" : "s"}`;
  return minutes ? `${h} ${minutes} minute${minutes === 1 ? "" : "s"}` : h;
}

/**
 * "in 2 hours" / "14 minutes ago", from an ISO instant.
 *
 * Only ever used next to a server-rendered absolute time, never instead of one: a
 * relative label is quick to scan but it is the absolute time in the account's
 * timezone that someone would act on.
 */
export function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = then - Date.now();
  const abs = Math.abs(diff);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  // Below a rounding error, both "0 seconds ago" and "in 0 seconds" read like a bug.
  // Seen on the setup checklist immediately after pressing Test connection.
  if (abs < 5_000) return "just now";

  const [value, unit] =
    abs < minute
      ? [Math.round(abs / 1000), "second"]
      : abs < hour
        ? [Math.round(abs / minute), "minute"]
        : abs < day
          ? [Math.round(abs / hour), "hour"]
          : [Math.round(abs / day), "day"];

  const plural = `${value} ${unit}${value === 1 ? "" : "s"}`;
  return diff >= 0 ? `in ${plural}` : `${plural} ago`;
}
